import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { sky } from './skyState';
import { createSkyMaterial } from './ProceduralSky';

// ── WHAT SHINY THINGS REFLECT ────────────────────────────────────────────────
//
// Replaces drei's <Environment> in SceneContents, and owns `scene.environment`
// for the life of the canvas.
//
// TWO MODES, ONE TEXTURE.
//   classic  the exact capture the world has always had: a near-black room with
//            the same four lightformers at the same places, intensities driven
//            by the same `tod` numbers. Interiors (and ?fx=off) use this, because
//            every mesh room's look was tuned under it — per-room values are not
//            ours to move (feedback_world_per_room_values).
//   sky      the street under the procedural sky: chrome, glass and the car's
//            paint reflect the sky that is actually overhead — blue at noon,
//            burning at golden hour — and at night the city's own neon strips
//            take over as the sky goes dark.
//
// The texture never changes identity and is never null. `USE_ENVMAP` and the
// cube-UV size are shader defines; swapping the env for a different-size map or
// unsetting it recompiles every material in the scene, synchronously, which is
// the stall PlanetScene's comments already warn about. Same 256² half-float cube
// drei used, so even the switch from drei to this compiles nothing new.
//
// Re-captured only when something changed: the sky moves ~1 envVersion per six
// game-minutes, a room swap flips the mode. A capture is six 256² renders of a
// handful of quads and one sphere, plus a PMREM blur — well under a frame.

function lightformer(scale, pos, rot) {
  const m = new THREE.Mesh(
    new THREE.PlaneGeometry(1, 1),
    new THREE.MeshBasicMaterial({ toneMapped: false, side: THREE.DoubleSide }),
  );
  m.scale.set(scale[0], scale[1], 1);
  m.position.set(...pos);
  m.rotation.set(...rot);
  return m;
}

export default function EnvProbe({ tod, mode = 'classic' }) {
  const gl = useThree((s) => s.gl);
  const scene = useThree((s) => s.scene);

  const rig = useMemo(() => {
    const vs = new THREE.Scene();
    const fbo = new THREE.WebGLCubeRenderTarget(256);
    fbo.texture.type = THREE.HalfFloatType;
    const cam = new THREE.CubeCamera(0.1, 1000, fbo);
    vs.add(cam);
    // The four lightformers, exactly as SceneContents declared them.
    const lf = [
      lightformer([60, 30], [0, 20, -40], [0, 0, 0]),
      lightformer([30, 1.2], [0, 5, -12], [Math.PI / 2, 0, 0]),
      lightformer([16, 1], [-16, 6, 8], [0, Math.PI / 2, 0]),
      lightformer([16, 1], [16, 7, 8], [0, -Math.PI / 2, 0]),
    ];
    lf.forEach((m) => vs.add(m));
    const skyMesh = new THREE.Mesh(new THREE.SphereGeometry(500, 32, 16), createSkyMaterial());
    skyMesh.frustumCulled = false;
    vs.add(skyMesh);
    return { vs, fbo, cam, lf, skyMesh, bg: new THREE.Color('#05060a') };
  }, []);

  useEffect(() => () => {
    rig.fbo.dispose();
    rig.lf.forEach((m) => { m.geometry.dispose(); m.material.dispose(); });
    rig.skyMesh.geometry.dispose(); rig.skyMesh.material.dispose();
  }, [rig]);

  // Claim the slot once, for good.
  useLayoutEffect(() => {
    const prev = scene.environment;
    const prevI = scene.environmentIntensity;
    scene.environment = rig.fbo.texture;
    return () => { scene.environment = prev; scene.environmentIntensity = prevI; };
  }, [scene, rig]);

  const capture = () => {
    const ac = gl.autoClear;
    gl.autoClear = true;
    rig.cam.update(gl, rig.vs);
    gl.autoClear = ac;
  };

  // Configure the virtual room for the current mode + hour, then capture.
  const lastVersion = useRef(-1);
  const configure = () => {
    const { lf, skyMesh, vs } = rig;
    if (mode === 'sky') {
      vs.background = null;
      skyMesh.visible = true;
      // The big sky panel is the sky itself now; the street strips stay as the
      // city's own light, and grow with the night.
      const n = sky.night;
      lf[0].visible = false;
      lf[1].material.color.set('#ffb25e').multiplyScalar(0.25 + n * 2.6);
      lf[2].material.color.set('#ff5e78').multiplyScalar(0.15 + n * 2.2);
      lf[3].material.color.set('#7dd3ff').multiplyScalar(0.15 + n * 2.2);
      scene.environmentIntensity = sky.envI;
    } else {
      vs.background = rig.bg;
      skyMesh.visible = false;
      lf[0].visible = true;
      const night = tod?.night ?? 0;
      lf[0].material.color.set(tod?.sky || '#000').multiplyScalar(0.6 + night * 0.5);
      lf[1].material.color.set('#ffb25e').multiplyScalar(1.2 + night * 1.6);
      lf[2].material.color.set('#ff5e78').multiplyScalar(0.9 + night * 1.4);
      lf[3].material.color.set('#7dd3ff').multiplyScalar(0.9 + night * 1.4);
      scene.environmentIntensity = 1;
    }
    capture();
    lastVersion.current = sky.envVersion;
  };

  // Classic follows `tod` (React, every 30 s); a mode switch always recaptures.
  useLayoutEffect(configure, [mode, tod?.sky, tod?.night]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sky mode follows the sky clock.
  useFrame(() => {
    if (mode !== 'sky') return;
    if (sky.envVersion !== lastVersion.current) configure();
    else if (scene.environmentIntensity !== sky.envI) scene.environmentIntensity = sky.envI;
  });

  return null;
}
